import { locationPath, serviceCityPath, servicePath } from '../enum/routes';
import { SERVICES, type FaqItem } from './services';

export type CityDef = {
  slug: string;
  name: string;
  state: string;
  regionLabel: string;
  metro: 'austin' | 'dallas' | 'houston' | 'san-antonio';
  priority: 'core' | 'metro';
  nearbySlugs: string[];
  metaTitle: string;
  metaDescription: string;
  heroLead: string;
  aboutTitle: string;
  aboutParagraphs: string[];
  localNotesTitle: string;
  localNotes: string[];
  faqs: FaqItem[];
  /** Per-service blurbs for /services/:service/:city pages - unique, not name-swaps */
  serviceNotes: Record<
    string,
    {
      heroLead: string;
      paragraphs: string[];
      localBullets: string[];
      metaTitle: string;
      metaDescription: string;
    }
  >;
};

export const CITIES: CityDef[] = [
  {
    slug: 'austin',
    name: 'Austin',
    state: 'TX',
    regionLabel: 'Travis County and surrounding metros',
    metro: 'austin',
    priority: 'core',
    nearbySlugs: ['dallas', 'houston', 'san-antonio'],
    metaTitle: 'Home Remodeling Quotes in Austin, TX | Epic Home Pros',
    metaDescription:
      'Free remodel estimates for Austin homeowners. Epic Home Pros helps homeowners plan kitchens, bathrooms, ADUs, and outdoor living.',
    heroLead:
      'Austin homeowners use Epic Home Pros to start kitchens, baths, ADUs, and outdoor living - free estimate, no obligation.',
    aboutTitle: 'Remodeling in Austin and nearby communities',
    aboutParagraphs: [
      'Planning a remodel in Austin should not mean calling contractor after contractor. Tell us what you want to build, your address area, and what you are envisioning. We follow up to schedule a visit, look at the space, and put together a quote.',
      'Central Texas homes range from mid-century ranch layouts to newer suburban builds. That mix affects kitchens, wet areas, backyard ADUs, and outdoor shade - which is why an on-site visit matters more than a phone guess.',
      'Free estimates. Local professionals. No pressure.',
    ],
    localNotesTitle: 'What Austin projects often involve',
    localNotes: [
      'Kitchen and bath updates in established neighborhoods where layouts and plumbing access vary house to house',
      'ADU interest on lots where setbacks, utilities, and parking rules need a local builder walk-through',
      'Outdoor living that plans for heat, sun, and summer storms - covered patios and shade structures are common requests',
      'Reply within one business day after you submit a quote request',
    ],
    faqs: [
      {
        question: 'Do you serve only central Austin?',
        answer:
          'We serve projects across the Austin metro and surrounding communities. Submit a quote with your city and we will confirm whether we can help.',
      },
      {
        question: 'What services can Austin homeowners request?',
        answer:
          'Kitchen remodeling, bathroom remodeling, ADU construction, and outdoor living - the same four services we cover across Texas.',
      },
    ],
    serviceNotes: {
      'kitchen-remodeling': {
        heroLead:
          'Austin kitchen remodeling quotes with free on-site estimates - from cosmetic refreshes to full guts.',
        paragraphs: [
          'Austin kitchens span tight mid-century footprints and open-concept suburban layouts. Share what you want to change and we will follow up to schedule a free on-site estimate.',
          'Typical Texas market ranges still apply as context; your Austin pro will price cabinetry, stone, plumbing, and layout for your house - not a national average.',
        ],
        localBullets: [
          'On-site measuring for islands, pantries, and open-concept connections',
          'On-site estimates familiar with Central Texas homes',
          'Written estimate after the visit - you decide next steps',
        ],
        metaTitle: 'Kitchen Remodeling in Austin, TX | Epic Home Pros',
        metaDescription:
          'Free kitchen remodeling estimates in Austin, TX. Epic Home Pros provides free on-site kitchen remodeling estimates.',
      },
      'bathroom-remodeling': {
        heroLead:
          'Austin bathroom remodeling - hall baths, primary suites, walk-in showers, and tub conversions.',
        paragraphs: [
          'Tell us hall bath, primary suite, or both. We schedule an on-site visit to measure wet walls and plumbing access before quoting.',
          'Waterproofing and plumbing moves are the usual cost swingers. You get a written estimate and choose whether to move forward.',
        ],
        localBullets: [
          'Walk-in showers and tub-to-shower conversions',
          'Tile, vanities, and waterproofing scoped on site',
          'Free estimate with no obligation',
        ],
        metaTitle: 'Bathroom Remodeling in Austin, TX | Epic Home Pros',
        metaDescription:
          'Free bathroom remodeling estimates in Austin. Free estimates for primary suites, hall baths, and walk-in showers.',
      },
      'adu-construction': {
        heroLead:
          'Austin ADU quotes with free on-site estimates who walk setbacks, utilities, and lot access.',
        paragraphs: [
          'Backyard cottages, garage conversions, and attached suites are permitting projects. We schedule a lot walk before design and pricing so setbacks and utilities are clear.',
          "Rules and feasibility vary by property. We'll tell you what is realistic for setbacks, utilities, and parking."
        ],
        localBullets: [
          'Detached, attached, and garage-conversion paths',
          'Lot walk before you commit to a build plan',
          'Written estimate - free, no obligation',
        ],
        metaTitle: 'ADU Construction in Austin, TX | Epic Home Pros',
        metaDescription:
          'Free ADU estimates in Austin, TX for backyard cottages, garage conversions, and in-law suites.',
      },
      'outdoor-living': {
        heroLead:
          'Austin outdoor living quotes for decks, covered patios, pergolas, and outdoor kitchens built for Central Texas weather.',
        paragraphs: [
          'Shade and heat drive many Austin backyard plans. Share deck, patio, pergola, or outdoor kitchen goals and we will follow up to schedule an on-site visit.',
          'They walk drainage, sun, and access, then quote materials suited to Texas weather. You decide whether to move forward.',
        ],
        localBullets: [
          'Cedar, composite, and hardwood deck options',
          'Covered patios and shade structures',
          'On-site pricing - not catalog photos alone',
        ],
        metaTitle: 'Outdoor Living in Austin, TX | Epic Home Pros',
        metaDescription:
          'Free outdoor living estimates in Austin. Decks, patios, pergolas, and outdoor kitchens with free on-site estimates.',
      },
    },
  },
  {
    slug: 'dallas',
    name: 'Dallas',
    state: 'TX',
    regionLabel: 'Dallas-Fort Worth',
    metro: 'dallas',
    priority: 'core',
    nearbySlugs: ['fort-worth', 'arlington', 'austin', 'houston'],
    metaTitle: 'Home Remodeling Quotes in Dallas, TX | Epic Home Pros',
    metaDescription:
      'Free remodel estimates for Dallas and DFW homeowners. Free estimates for kitchens, bathrooms, ADUs, and outdoor living.',
    heroLead:
      'Dallas and DFW homeowners start kitchens, baths, ADUs, and outdoor projects here - with free on-site estimates with a free on-site estimate.',
    aboutTitle: 'Remodeling across Dallas-Fort Worth',
    aboutParagraphs: [
      'DFW housing stock is broad: older in-town homes, suburban builds, and everything in between. Epic Home Pros helps you skip the cold-call loop and schedule a visit for a written quote.',
      'Whether you are updating a kitchen, reworking a primary bath, exploring an ADU, or adding a covered patio, the first step is the same: share project details and location, then schedule an on-site visit.',
      'Free estimates. Local professionals. No pressure.',
    ],
    localNotesTitle: 'What Dallas-area projects often involve',
    localNotes: [
      'Kitchen and bath remodels where existing plumbing and HVAC chases vary by era of home',
      'Outdoor living scoped for North Texas heat and storm seasons',
      'ADU conversations that start with lot access, setbacks, and utilities',
      'Coverage that can extend toward Fort Worth and Arlington - confirm with a quote request',
    ],
    faqs: [
      {
        question: 'Do you cover the whole DFW area?',
        answer:
          'We cover projects in Dallas and surrounding DFW communities including Fort Worth and Arlington. Submit your address area and we will confirm coverage.',
      },
      {
        question: 'How quickly will someone respond in Dallas?',
        answer: 'We aim to reply within one business day after your quote request.',
      },
    ],
    serviceNotes: {
      'kitchen-remodeling': {
        heroLead:
          'Dallas kitchen remodeling quotes with on-site measuring for your layout.',
        paragraphs: [
          'From cosmetic refreshes to structural guts, Dallas kitchens need pricing based on the house - cabinetry, counters, plumbing, and electrical. We schedule an on-site visit to price the kitchen.',
          'Share photos and goals in the quote form. You get an on-site visit and a written estimate with no obligation.',
        ],
        localBullets: [
          'DFW-based licensed kitchen remodelers',
          'On-site estimate - not a phone guess',
          'Typical Texas cost ranges as context; your house sets the number',
        ],
        metaTitle: 'Kitchen Remodeling in Dallas, TX | Epic Home Pros',
        metaDescription:
          'Free kitchen remodeling estimates in Dallas and DFW with on-site quotes.',
      },
      'bathroom-remodeling': {
        heroLead:
          'Dallas bathroom remodeling for hall baths, primary suites, and walk-in shower projects.',
        paragraphs: [
          'Tell us the room and scope. We schedule an on-site visit to check waterproofing and plumbing access before quoting.',
          'Written estimate after the visit. Free, with no obligation to hire.',
        ],
        localBullets: [
          'Tub-to-shower conversions and primary suites',
          'Tile and waterproofing scoped on site',
          'Reply within one business day',
        ],
        metaTitle: 'Bathroom Remodeling in Dallas, TX | Epic Home Pros',
        metaDescription:
          'Free bathroom remodeling estimates in Dallas, TX. Free on-site estimates for hall baths and primary suites.',
      },
      'adu-construction': {
        heroLead:
          'Dallas ADU construction quotes grounded in local lot and utility realities.',
        paragraphs: [
          'Detached cottages, garage conversions, and attached suites start with a lot walk. We schedule a lot walk for the Dallas metro.',
          'Feasibility depends on setbacks, utilities, and parking. Your pro will outline a realistic path through design and permits.',
        ],
        localBullets: [
          'Conversion and new-build paths',
          'Local permitting and site awareness',
          'Free written estimate after the visit',
        ],
        metaTitle: 'ADU Construction in Dallas, TX | Epic Home Pros',
        metaDescription:
          'Free ADU estimates in Dallas, TX for backyard homes and garage conversions.',
      },
      'outdoor-living': {
        heroLead:
          'Dallas outdoor living - decks, patios, pergolas, and outdoor kitchens quoted for North Texas weather.',
        paragraphs: [
          'Share the project type and rough size. We schedule a visit to walk sun, drainage, and access on your Dallas-area lot.',
          'Materials and cover structures are priced for how Texans actually use a backyard - not a catalog photo alone.',
        ],
        localBullets: [
          'Decks, covered patios, and shade structures',
          'Outdoor kitchens with gas/electrical considerations',
          'On-site written estimate',
        ],
        metaTitle: 'Outdoor Living in Dallas, TX | Epic Home Pros',
        metaDescription:
          'Free outdoor living estimates in Dallas. Decks, patios, pergolas, and outdoor kitchens with licensed local builders.',
      },
    },
  },
  {
    slug: 'houston',
    name: 'Houston',
    state: 'TX',
    regionLabel: 'Harris County and surrounding metros',
    metro: 'houston',
    priority: 'core',
    nearbySlugs: ['pasadena', 'pearland', 'sugar-land', 'the-woodlands', 'dallas', 'austin'],
    metaTitle: 'Home Remodeling Quotes in Houston, TX | Epic Home Pros',
    metaDescription:
      'Free remodel estimates for Houston-area homeowners. Free estimates for kitchens, bathrooms, ADUs, and outdoor living.',
    heroLead:
      'Houston and nearby communities start remodel projects here - kitchens, baths, ADUs, and outdoor living with free on-site estimates.',
    aboutTitle: 'Remodeling in Greater Houston',
    aboutParagraphs: [
      'Houston-area homes deal with humidity, drainage, and a wide mix of housing ages. Epic Home Pros helps you request one quote and schedule a visit to price the work.',
      'From inside remodels to backyard ADUs and covered outdoor rooms, the process stays simple: share details, schedule a visit, review a written estimate, and decide.',
      'Free estimates. Local professionals. No pressure.',
    ],
    localNotesTitle: 'What Houston-area projects often involve',
    localNotes: [
      'Kitchen and bath work where humidity and waterproofing details matter',
      'Outdoor living planned with drainage and summer storms in mind',
      'ADU interest that starts with lot access, utilities, and local rules',
      'Nearby cities such as Pasadena, Pearland, Sugar Land, and The Woodlands - confirm coverage with a quote',
    ],
    faqs: [
      {
        question: 'Do you serve Houston suburbs?',
        answer:
          'Yes - we cover projects in Houston and surrounding communities including Pasadena, Pearland, Sugar Land, and The Woodlands when coverage allows. Request a quote to confirm.',
      },
      {
        question: 'What should Houston homeowners prepare for a visit?',
        answer:
          'Project goals, preferred timing, and photos if you have them. For outdoor or ADU work, notes about drainage and yard access help.',
      },
    ],
    serviceNotes: {
      'kitchen-remodeling': {
        heroLead:
          'Houston kitchen remodeling quotes with on-site pricing for your layout.',
        paragraphs: [
          'Whether you want a refresh or a full gut, we schedule an on-site visit for measuring, layout talk, and a written estimate.',
          'Texas market ranges are context only. Humidity, ventilation, and existing plumbing all factor into a house-specific number.',
        ],
        localBullets: [
          'Cosmetic through structural kitchen scopes',
          'On-site visit in the Houston metro',
          'Free estimate, no obligation',
        ],
        metaTitle: 'Kitchen Remodeling in Houston, TX | Epic Home Pros',
        metaDescription:
          'Free kitchen remodeling estimates in Houston, TX with on-site quotes.',
      },
      'bathroom-remodeling': {
        heroLead:
          'Houston bathroom remodeling - waterproofing-aware scopes for hall baths and primary suites.',
        paragraphs: [
          'Share hall bath, primary suite, or both. We schedule an on-site visit to check wet walls and plumbing access before quoting.',
          'You receive a written estimate and decide whether to move forward.',
        ],
        localBullets: [
          'Walk-in showers and tub conversions',
          'Tile and waterproofing scoped carefully',
          'Reply within one business day',
        ],
        metaTitle: 'Bathroom Remodeling in Houston, TX | Epic Home Pros',
        metaDescription:
          'Free bathroom remodeling estimates in Houston. Free on-site estimates for primary suites and hall baths.',
      },
      'adu-construction': {
        heroLead:
          'Houston ADU quotes with free on-site estimates who walk setbacks, utilities, and site access.',
        paragraphs: [
          'Garage conversions and detached backyard homes need a clear path through design and permits. We schedule a lot walk for the Houston metro.',
          'Your pro will walk the lot and outline what is realistic before you commit.',
        ],
        localBullets: [
          'Detached, attached, and conversion options',
          'Local site and utility evaluation',
          'Free written estimate',
        ],
        metaTitle: 'ADU Construction in Houston, TX | Epic Home Pros',
        metaDescription:
          'Free ADU estimates in Houston, TX for backyard homes and conversions.',
      },
      'outdoor-living': {
        heroLead:
          'Houston outdoor living quotes for decks, covered patios, and outdoor kitchens planned for Gulf Coast weather patterns.',
        paragraphs: [
          'Drainage and shade matter on Houston lots. Tell us deck, patio, pergola, or outdoor kitchen goals and we will schedule an on-site outdoor living visit.',
          'They walk the site and quote materials for Texas heat and rain - you decide next steps.',
        ],
        localBullets: [
          'Covered patios and shade structures',
          'Decks and outdoor kitchens',
          'On-site drainage and access review',
        ],
        metaTitle: 'Outdoor Living in Houston, TX | Epic Home Pros',
        metaDescription:
          'Free outdoor living estimates in Houston. Decks, patios, pergolas, and outdoor kitchens with licensed local builders.',
      },
    },
  },
  {
    slug: 'san-antonio',
    name: 'San Antonio',
    state: 'TX',
    regionLabel: 'Bexar County and surrounding metros',
    metro: 'san-antonio',
    priority: 'core',
    nearbySlugs: ['austin', 'houston', 'dallas'],
    metaTitle: 'Home Remodeling Quotes in San Antonio, TX | Epic Home Pros',
    metaDescription:
      'Free remodel estimates for San Antonio homeowners. Free estimates for kitchens, bathrooms, ADUs, and outdoor living.',
    heroLead:
      'San Antonio homeowners use Epic Home Pros to start kitchens, baths, ADUs, and outdoor living with free estimates.',
    aboutTitle: 'Remodeling in San Antonio and nearby areas',
    aboutParagraphs: [
      'From established neighborhoods to newer suburban streets, San Antonio projects benefit from an on-site look at the house. Tell us what you are planning and we will schedule a visit and written quote.',
      'Kitchen and bath updates, backyard ADUs, and outdoor shade structures are common starting points. One form. An on-site visit. You decide what happens next.',
      'Free estimates. Local professionals. No pressure.',
    ],
    localNotesTitle: 'What San Antonio projects often involve',
    localNotes: [
      'Kitchen and bath remodels tailored to existing footprints and plumbing access',
      'Outdoor living planned for South Texas sun and heat',
      'ADU conversations that begin with lot constraints and utilities',
      'Clear next steps after a free on-site estimate',
    ],
    faqs: [
      {
        question: 'Which services are available in San Antonio?',
        answer:
          'Kitchen remodeling, bathroom remodeling, ADU construction, and outdoor living.',
      },
      {
        question: 'Is there an obligation after the estimate?',
        answer: 'No. Estimates are free and you are not obligated to move forward.',
      },
    ],
    serviceNotes: {
      'kitchen-remodeling': {
        heroLead:
          'San Antonio kitchen remodeling quotes with free estimates from professionals.',
        paragraphs: [
          'Share refresh vs. full remodel goals. We schedule an on-site visit, measure, and prepare a written estimate for your San Antonio home.',
          'Use Texas market ranges as context; your house and finish choices set the real number.',
        ],
        localBullets: [
          'Cabinetry, counters, appliances, and layout changes',
          'On-site visit and written estimate',
          'No obligation after the quote',
        ],
        metaTitle: 'Kitchen Remodeling in San Antonio, TX | Epic Home Pros',
        metaDescription:
          'Free kitchen remodeling estimates in San Antonio, TX.',
      },
      'bathroom-remodeling': {
        heroLead:
          'San Antonio bathroom remodeling for hall baths, primary suites, and shower conversions.',
        paragraphs: [
          'Tell us the room type and priorities. A licensed local bath remodeler will check waterproofing and plumbing access, then quote in writing.',
        ],
        localBullets: [
          'Walk-in showers and vanity updates',
          'Primary suite and hall bath scopes',
          'Free estimate within our standard process',
        ],
        metaTitle: 'Bathroom Remodeling in San Antonio, TX | Epic Home Pros',
        metaDescription:
          'Free bathroom remodeling estimates in San Antonio. Free on-site estimates for hall baths and primary suites.',
      },
      'adu-construction': {
        heroLead:
          'San Antonio ADU quotes with local builders who evaluate lot, setbacks, and utilities first.',
        paragraphs: [
          'Detached cottages, garage conversions, and attached suites need a realistic permitting path. We schedule a lot walk for the San Antonio metro.',
        ],
        localBullets: [
          'Lot walk before design lock-in',
          'Conversion and new-build options',
          'Free written estimate',
        ],
        metaTitle: 'ADU Construction in San Antonio, TX | Epic Home Pros',
        metaDescription:
          'Free ADU estimates in San Antonio, TX for backyard homes and conversions.',
      },
      'outdoor-living': {
        heroLead:
          'San Antonio outdoor living - decks, patios, pergolas, and kitchens built for South Texas sun.',
        paragraphs: [
          'Shade is often the priority. Share your project type and we will schedule an on-site walk and written estimate.',
        ],
        localBullets: [
          'Covered patios and pergolas',
          'Decks and outdoor kitchens',
          'Materials chosen for heat and sun',
        ],
        metaTitle: 'Outdoor Living in San Antonio, TX | Epic Home Pros',
        metaDescription:
          'Free outdoor living estimates in San Antonio. Decks, patios, pergolas, and outdoor kitchens with local builders.',
      },
    },
  },
  {
    slug: 'fort-worth',
    name: 'Fort Worth',
    state: 'TX',
    regionLabel: 'Dallas-Fort Worth',
    metro: 'dallas',
    priority: 'metro',
    nearbySlugs: ['dallas', 'arlington'],
    metaTitle: 'Home Remodeling Quotes in Fort Worth, TX | Epic Home Pros',
    metaDescription:
      'Free remodel estimates for Fort Worth homeowners. Epic Home Pros helps homeowners plan kitchens, baths, ADUs, and outdoor living.',
    heroLead:
      'Fort Worth homeowners can request free remodel estimates for kitchens, bathrooms, ADUs, and outdoor living - with free on-site estimates.',
    aboutTitle: 'Starting a remodel in Fort Worth',
    aboutParagraphs: [
      'Fort Worth is covered alongside Dallas in our DFW service area. Tell us your project and neighborhood area; we confirm coverage and schedule an on-site estimate.',
      'Free estimates. No obligation. Reply within one business day.',
    ],
    localNotesTitle: 'Fort Worth notes',
    localNotes: [
      'Kitchen and bath projects priced after an on-site visit',
      'Outdoor living scoped for North Texas weather',
      'ADU feasibility depends on lot and local rules - a builder walk comes first',
      'Nearby Dallas and Arlington pages for related metro info',
    ],
    faqs: [
      {
        question: 'Is Fort Worth covered the same way as Dallas?',
        answer:
          'Fort Worth is part of our DFW service area. Submit a quote with your address and we will confirm coverage.',
      },
    ],
    serviceNotes: {},
  },
  {
    slug: 'arlington',
    name: 'Arlington',
    state: 'TX',
    regionLabel: 'Dallas-Fort Worth',
    metro: 'dallas',
    priority: 'metro',
    nearbySlugs: ['dallas', 'fort-worth'],
    metaTitle: 'Home Remodeling Quotes in Arlington, TX | Epic Home Pros',
    metaDescription:
      'Free remodel estimates for Arlington, TX homeowners. Free estimates for kitchens, bathrooms, ADUs, and outdoor living.',
    heroLead:
      'Arlington sits between Dallas and Fort Worth - request a free estimate and we will follow up to schedule next steps.',
    aboutTitle: 'Remodeling in Arlington',
    aboutParagraphs: [
      'Share your kitchen, bath, ADU, or outdoor living plans. Epic Home Pros helps Arlington-area homeowners schedule a visit and written quote.',
      'Confirm coverage with a quote request. Free estimates, no pressure.',
    ],
    localNotesTitle: 'Arlington notes',
    localNotes: [
      'Mid-cities location in our DFW service area',
      'Same four services: kitchen, bath, ADU, outdoor living',
      'On-site estimates rather than phone-only pricing',
    ],
    faqs: [
      {
        question: 'Which services are available in Arlington?',
        answer:
          'Kitchen remodeling, bathroom remodeling, ADU construction, and outdoor living when we have coverage for your address.',
      },
    ],
    serviceNotes: {},
  },
  {
    slug: 'pasadena',
    name: 'Pasadena',
    state: 'TX',
    regionLabel: 'Greater Houston',
    metro: 'houston',
    priority: 'metro',
    nearbySlugs: ['houston', 'pearland', 'sugar-land'],
    metaTitle: 'Home Remodeling Quotes in Pasadena, TX | Epic Home Pros',
    metaDescription:
      'Free remodel estimates for Pasadena, TX for kitchens, baths, ADUs, and outdoor living.',
    heroLead:
      'Pasadena homeowners can start kitchen, bath, ADU, and outdoor projects with a free Houston-area estimate.',
    aboutTitle: 'Remodeling in Pasadena',
    aboutParagraphs: [
      'Pasadena is part of our Greater Houston service area. Submit project details and we will confirm whether we can schedule an on-site quote.',
    ],
    localNotesTitle: 'Pasadena notes',
    localNotes: [
      'Houston-metro coverage for the four core services',
      'Outdoor and ADU visits should account for site drainage and access',
      'Related pages for Houston, Pearland, and Sugar Land',
    ],
    faqs: [
      {
        question: 'Do you serve Pasadena for all services?',
        answer:
          'We cover the same services as Houston when coverage allows. Request a quote to confirm for your address.',
      },
    ],
    serviceNotes: {},
  },
  {
    slug: 'pearland',
    name: 'Pearland',
    state: 'TX',
    regionLabel: 'Greater Houston',
    metro: 'houston',
    priority: 'metro',
    nearbySlugs: ['houston', 'pasadena', 'sugar-land'],
    metaTitle: 'Home Remodeling Quotes in Pearland, TX | Epic Home Pros',
    metaDescription:
      'Free remodel estimates for Pearland, TX homeowners. Kitchen, bath, ADU, and outdoor living with free on-site estimates.',
    heroLead:
      'Pearland homeowners: request a free remodel estimate for kitchens, baths, ADUs, or outdoor living.',
    aboutTitle: 'Remodeling in Pearland',
    aboutParagraphs: [
      'As part of Greater Houston coverage, Pearland projects follow the same simple path: one request, an on-site estimate, and no obligation.',
    ],
    localNotesTitle: 'Pearland notes',
    localNotes: [
      'Suburban lots where outdoor living and kitchen open-concepts are common requests',
      'Confirm ADU feasibility with a local builder lot walk',
      'Link out to Houston and nearby suburb pages for more metro context',
    ],
    faqs: [
      {
        question: 'How do I start a Pearland quote?',
        answer:
          'Use the free quote form with your city and project type, or call us. We aim to reply within one business day.',
      },
    ],
    serviceNotes: {},
  },
  {
    slug: 'sugar-land',
    name: 'Sugar Land',
    state: 'TX',
    regionLabel: 'Greater Houston',
    metro: 'houston',
    priority: 'metro',
    nearbySlugs: ['houston', 'pearland', 'the-woodlands'],
    metaTitle: 'Home Remodeling Quotes in Sugar Land, TX | Epic Home Pros',
    metaDescription:
      'Free remodel estimates for Sugar Land, TX. Free estimates for kitchens, bathrooms, ADUs, and outdoor living.',
    heroLead:
      'Sugar Land homeowners can request free estimates for kitchen, bath, ADU, and outdoor living projects with free on-site estimates.',
    aboutTitle: 'Remodeling in Sugar Land',
    aboutParagraphs: [
      'Share what you want to change and we will schedule a visit and written quote when coverage allows.',
    ],
    localNotesTitle: 'Sugar Land notes',
    localNotes: [
      'Kitchen and bath updates priced after measuring the existing home',
      'Outdoor living often includes shade and covered patio scopes',
      'ADU projects start with lot and utility review',
    ],
    faqs: [
      {
        question: 'Is Sugar Land covered under Houston?',
        answer:
          'Sugar Land is part of our Greater Houston service area. Submit a quote to confirm service for your address.',
      },
    ],
    serviceNotes: {},
  },
  {
    slug: 'the-woodlands',
    name: 'The Woodlands',
    state: 'TX',
    regionLabel: 'Greater Houston',
    metro: 'houston',
    priority: 'metro',
    nearbySlugs: ['houston', 'sugar-land', 'pearland'],
    metaTitle: 'Home Remodeling Quotes in The Woodlands, TX | Epic Home Pros',
    metaDescription:
      'Free remodel estimates for The Woodlands, TX. Kitchen, bath, ADU, and outdoor living with free estimates from professionals.',
    heroLead:
      'The Woodlands homeowners: start your kitchen, bath, ADU, or outdoor living project with a free estimate.',
    aboutTitle: 'Remodeling in The Woodlands',
    aboutParagraphs: [
      'We cover The Woodlands-area projects through our Greater Houston service area when coverage allows. One form, a local visit, a written estimate - you decide next steps.',
    ],
    localNotesTitle: 'The Woodlands notes',
    localNotes: [
      'Outdoor living and kitchen remodels are frequent starting points',
      'HOA or community guidelines may affect exterior work - the site visit can help you plan around community rules',
      'Nearby Houston metro pages for additional service detail',
    ],
    faqs: [
      {
        question: 'Can I request an outdoor living quote in The Woodlands?',
        answer:
          'Yes. Choose outdoor living on the quote form and include your city. We will confirm coverage.',
      },
    ],
    serviceNotes: {},
  },
];

export const CITY_BY_SLUG = Object.fromEntries(CITIES.map((c) => [c.slug, c])) as Record<
  string,
  CityDef
>;

export const CORE_CITIES = CITIES.filter((c) => c.priority === 'core');
export const METRO_CITIES = CITIES.filter((c) => c.priority === 'metro');

export function getCity(slug: string): CityDef | undefined {
  return CITY_BY_SLUG[slug];
}

/** Core cities get full service×city pages; metro cities link to city + service hubs */
export function hasServiceCityPage(serviceSlug: string, citySlug: string): boolean {
  const city = getCity(citySlug);
  if (!city || city.priority !== 'core') return false;
  return Boolean(city.serviceNotes[serviceSlug]);
}

export function allServiceCityPaths(): { serviceSlug: string; citySlug: string; path: string }[] {
  const paths: { serviceSlug: string; citySlug: string; path: string }[] = [];
  for (const city of CORE_CITIES) {
    for (const service of SERVICES) {
      if (city.serviceNotes[service.slug]) {
        paths.push({
          serviceSlug: service.slug,
          citySlug: city.slug,
          path: serviceCityPath(service.slug, city.slug),
        });
      }
    }
  }
  return paths;
}

export { locationPath, servicePath, serviceCityPath };
